import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  WebinarSchedule,
  JITSchedule,
  WebinarLoginConfig,
  WebinarVideo,
  WebinarOffer,
  ChatMessage,
  SalesNotification,
  AudienceMode,
  IntegrationConfig,
  ChatbotRule
} from './types';

export interface WizardStep {
  id: number;
  title: string;
  completed: boolean;
}

export interface WizardData {
  // Step 1 - Início
  name: string;
  title: string;
  slug: string;
  language: string;
  easy_access: boolean;
  sync_with_time: boolean;

  // Step 2 - Agendamento
  schedule_mode: 'scheduled' | 'jit';
  scheduled_config?: WebinarSchedule;
  jit_config?: JITSchedule;

  // Step 3 - Login
  login_config: Partial<WebinarLoginConfig>;

  // Step 4 - Vídeo
  video: {
    videoId?: string;
    videoTitle?: string;
    videoThumbnail?: string;
    videoDuration?: number;
    autoplay?: boolean;
  };

  // Step 5 - Oferta
  offer: Partial<WebinarOffer>;

  // Step 6 - Chat
  chat_messages: ChatMessage[];
  chat_pacing_min: number;
  chat_pacing_max: number;

  // Step 7 - Vendas
  sales_notifications: SalesNotification[];

  // Step 8 - Audiência
  audience_mode: AudienceMode;
  audience_base_value?: number;
  audience_variance_rules?: any[];

  // Step 9 - Integrações
  integrations: {
    [key: string]: IntegrationConfig;
  };

  // Step 10 - Chatbot
  chatbot_rules: ChatbotRule[];
}

export interface WizardStore {
  currentStep: number;
  steps: WizardStep[];
  data: WizardData;
  webinarId?: string;

  // Actions
  setCurrentStep: (step: number) => void;
  nextStep: () => void;
  prevStep: () => void;
  updateData: (field: keyof WizardData, value: any) => void;
  updateNestedData: (field: keyof WizardData, subfield: string, value: any) => void;
  markStepCompleted: (step: number) => void;
  resetWizard: () => void;
  setWebinarId: (id: string) => void;
}

const initialData: WizardData = {
  name: '',
  title: '',
  slug: '',
  language: 'pt-BR',
  easy_access: false,
  sync_with_time: false,
  schedule_mode: 'scheduled',
  scheduled_config: {
    start_at: '',
    duration_min: 60,
    timezone: typeof Intl !== 'undefined' ? Intl.DateTimeFormat().resolvedOptions().timeZone : 'America/Sao_Paulo',
    late_join_minutes: 15
  },
  jit_config: {
    days_of_week: ['mon', 'tue', 'wed', 'thu', 'fri'],
    day_start: '08:00',
    day_end: '22:00',
    interval_min: 10,
    min_wait_min: 5,
    display_slots: 1,
    timezone: typeof Intl !== 'undefined' ? Intl.DateTimeFormat().resolvedOptions().timeZone : 'America/Sao_Paulo'
  },
  login_config: {
    // Identidade visual
    logo_alignment: 'center',
    background_color: '#ffffff',
    background_blur: false,
    
    // Indicador de disponibilidade
    availability_type: 'line',
    availability_thickness: 4,
    availability_color_base: '#e5e7eb',
    availability_color_progress: '#6366f1',
    availability_initial: 0,
    availability_label: 'Restam poucas vagas',
    
    // Título e subtítulo
    page_title: '[AO VIVO] Crie seu webinário do zero em 60 minutos',
    page_subtitle: 'Treinamento prático para criar, configurar e abrir sua sala',
    
    // CTA
    cta_label: 'Entrar na Sala',
    cta_style: 'solid',
    cta_behavior: 'submit_enter',
    
    // Campos do formulário
    form_fields: [
      { id: '1', name: 'name', label: 'Como podemos te chamar?', type: 'text', required: true, placeholder: 'Seu nome', enabled: true },
      { id: '2', name: 'email', label: 'Seu melhor e-mail', type: 'email', required: true, placeholder: 'seu@email.com', enabled: true },
      { id: '3', name: 'whatsapp', label: 'WhatsApp com DDD', type: 'tel', required: false, placeholder: '(11) 99999-9999', enabled: true }
    ],
    extra_fields: [],
    
    // LGPD
    lgpd_enabled: true,
    lgpd_text: 'Concordo em receber comunicações sobre este evento. Sem spam.',
    lgpd_required: true,
    
    // Elementos de confiança
    trust_seals_enabled: true,
    trust_seals: [
      { id: '1', icon: 'Shield', label: 'Site Seguro', enabled: true },
      { id: '2', icon: 'Lock', label: 'Privacidade Protegida', enabled: true },
      { id: '3', icon: 'Mail', label: 'Sem Spam', enabled: true }
    ],
    footer_links: [
      { id: '1', label: 'Política de Privacidade', url: '/privacidade' },
      { id: '2', label: 'Termos de Uso', url: '/termos' },
      { id: '3', label: 'Suporte', url: '/suporte' }
    ],
    
    // Aprimoradores
    countdown_enabled: false,
    testimonial_enabled: false,
    timezone_indicator: true,
    event_badge_enabled: true
  },
  video: {
    autoplay: false
  },
  offer: {
    internal_name: '',
    title: 'Oferta especial do evento',
    subtitle: 'Válida apenas durante o webinário',
    cta1_label: 'Quero aproveitar',
    cta1_url: '',
    cta1_color: '#6366f1',
    cta1_target: 'new',
    cta2_enabled: false,
    timer_enabled: false,
    timer_label: 'Termina em:',
    timer_action: 'keep',
    badges: {
      limited_spots: false,
      today_only: false,
      bonus_time: false
    },
    offer_start_sec: 0,
    reshow_at_end: false,
    modal_position: 'center',
    modal_allow_close: true,
    modal_close_delay_sec: 0,
    modal_sticky_bar: false,
    chat_auto_message: false,
    chat_message_text: '🚀 A oferta foi liberada! O botão já está disponível.',
    trigger_sales_simulation: false,
    coupon_enabled: false,
    raffle_enabled: false
  },
  chat_messages: [],
  chat_pacing_min: 10,
  chat_pacing_max: 20,
  sales_notifications: [],
  audience_mode: 'hidden',
  integrations: {},
  chatbot_rules: []
};

const initialSteps: WizardStep[] = [
  { id: 1, title: 'Projeto', completed: false },
  { id: 2, title: 'Agendamento', completed: false },
  { id: 3, title: 'Inscrição', completed: false },
  { id: 4, title: 'Mídia', completed: false },
  { id: 5, title: 'Oferta', completed: false },
  { id: 6, title: 'Chat & Reações', completed: false },
  { id: 7, title: 'Prova Social', completed: false },
  { id: 8, title: 'Audiência', completed: false },
  { id: 9, title: 'Integrações', completed: false },
  { id: 10, title: 'Automação', completed: false }
];

export const useWizardStore = create<WizardStore>()(
  persist(
    (set, get) => ({
      currentStep: 1,
      steps: initialSteps,
      data: initialData,

      setCurrentStep: (step: number) => {
        set({ currentStep: step });
      },

      nextStep: () => {
        const { currentStep, steps } = get();
        if (currentStep < steps.length) {
          set({ currentStep: currentStep + 1 });
        }
      },

      prevStep: () => {
        const { currentStep } = get();
        if (currentStep > 1) {
          set({ currentStep: currentStep - 1 });
        }
      },

      updateData: (field: keyof WizardData, value: any) => {
        set(state => ({
          data: {
            ...state.data,
            [field]: value
          }
        }));
      },

      updateNestedData: (field: keyof WizardData, subfield: string, value: any) => {
        set(state => ({
          data: {
            ...state.data,
            [field]: {
              ...(state.data[field] as any),
              [subfield]: value
            }
          }
        }));
      },

      markStepCompleted: (step: number) => {
        set(state => ({
          steps: state.steps.map(s => 
            s.id === step ? { ...s, completed: true } : s
          )
        }));
      },

      resetWizard: () => {
        set({
          currentStep: 1,
          steps: initialSteps,
          data: initialData,
          webinarId: undefined
        });
      },

      setWebinarId: (id: string) => {
        set({ webinarId: id });
      }
    }),
    {
      name: 'webinar-wizard-storage'
    }
  )
);
